import {
  Badge,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
  Switch,
  Divider,
} from "antd";
import { useEffect, useState } from "react";
import { create, update } from "../../../services/expenses.service";
import { getAll as loadCategories } from "../../../services/categories.service";
import { AlertService } from "../../../services/alert.service";
import { useMutation, useQuery } from "react-query";
import { DatePickerProps } from "antd/lib";
import moment from "moment";
import dayjs from "dayjs";
import { frequencies } from "../../../Enums/Frequency.enums";

const { Text } = Typography;
const { TextArea } = Input;

export default function ExpenseForm(props: any) {
  const [categories, setCategories] = useState<any[]>([]);
  const [date, setDate] = useState<string>("");
  const [dueDate, setDueDate] = useState<string>("");
  const [paymentDate, setPaymentDate] = useState<string>("");
  const [isPaid, setIsPaid] = useState<boolean>(false);
  const [isRecurring, setIsRecurring] = useState<boolean>(false);

  const [formExpense] = Form.useForm();
  const paid = Form.useWatch("paid", formExpense);
  const recurring = Form.useWatch("is_recurring", formExpense);

  useEffect(() => {
    setIsPaid(paid);
  }, [paid]);

  useEffect(() => {
    setIsRecurring(recurring);
  }, [recurring]);

  useQuery(["loadCategories"], () => loadCategories(), {
    onSuccess: (response) =>
      setCategories(
        response.data.map((category: any) => {
          return {
            key: category.data.id,
            ...category.data,
          };
        })
      ),
    refetchOnWindowFocus: false,
  });

  const { mutate: createExpense, isLoading: createExpenseLoading } =
    useMutation(create, {
      onSuccess: () => props.savedForm("Nova(s) despesa(s) gravada(s) com sucesso."),
    });

  const { mutate: updateExpense, isLoading: updateExpenseLoading } =
    useMutation(update, {
      onSuccess: () => props.savedForm("Despesa editada com sucesso."),
    });

  const onChangeDate: DatePickerProps["onChange"] = (date, dateString) => {
    setDate(moment(dateString, "DD/MM/YYYY").format("YYYY-MM-DD"));
  };

  const onChangeDueDate: DatePickerProps["onChange"] = (date, dateString) => {
    setDueDate(moment(dateString, "DD/MM/YYYY").format("YYYY-MM-DD"));
  };

  const onChangePaymentDate: DatePickerProps["onChange"] = (date, dateString) => {
    setPaymentDate(moment(dateString, "DD/MM/YYYY").format("YYYY-MM-DD"));
  };

  const onSubmit = () => {
    formExpense
      .validateFields()
      .then((values) => {
        values.date =
          typeof date == "object" ? dayjs(date).format("YYYY-MM-DD") : date;

        if (values.due_date) {
            values.due_date = typeof dueDate == "object" ? dayjs(dueDate).format("YYYY-MM-DD") : dueDate;
        }

        if (values.payment_date) {
            values.payment_date = typeof paymentDate == "object" ? dayjs(paymentDate).format("YYYY-MM-DD") : paymentDate;
        }

        if (!props.expense || props.isDuplicate) {
          createExpense(values);
        } else {
          updateExpense({ values, id: Number(props.expense.id) });
        }
      })
      .catch(() =>
        AlertService.sendAlert([
          { text: "Campo(s) com preenchimento inválido", type: "error" },
        ])
      );
  };

  useEffect(() => {
    if (props.expense) {
      let values = {...props.expense};

      if (values.date) {
        values.date = dayjs(values?.date);
        setDate(values?.date);
      }

      if (values.due_date) {
        values.due_date = dayjs(values?.due_date);
        setDueDate(values?.due_date);
      }

      if (values.payment_date) {
        values.payment_date = dayjs(values?.payment_date);
        setPaymentDate(values?.payment_date);
      }
      
      formExpense.setFieldsValue(values);
    }
  }, [props.expense]);

  return (
    <>
      <div>
        <Form
          form={formExpense}
          name="form_table"
          layout="vertical"
          className="form_table"
          autoComplete="off"
          onValuesChange={() => props.onValuesChange(true)}
        >
          <Row gutter={20}>
            <Col span={24}>
              <Form.Item
                label="Despesa"
                name="description"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Input
                  placeholder="Adiciona o nome da despesa"
                  size="large"
                  maxLength={100}
                />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 12 }}
              md={{ span: 12 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                label="Valor"
                name="amount"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <InputNumber
                  min={0}
                  size="large"
                  placeholder="Valor"
                  maxLength={16}
                  addonAfter={<Text style={{ color: "#8C8C8C" }}>R$</Text>}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 12 }}
              md={{ span: 12 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                name="date"
                label="Data"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <DatePicker
                  size="large"
                  placeholder="Data"
                  showNow={true}
                  format={"DD/MM/YYYY"}
                  style={{ width: "100%" }}
                  onChange={onChangeDate}
                />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 24 }}
              md={{ span: 24 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                label="Categoria"
                name="category_id"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Select placeholder="Selecione a categoria" size="large">
                  {categories.map((category: any) => (
                    <Select.Option key={category.id} value={category.id} disabled={!category.active}>
                      <Badge color={category.color} text={category.name} />
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Observações" name="notes">
                <TextArea size="large" showCount maxLength={255} />
              </Form.Item>
            </Col>

            <Divider />

            <Col
                lg={{ span: 6 }}
                md={{ span: 6 }}
                sm={{ span: 12 }}
                xs={{ span: 12 }}
            >
                <Form.Item
                    name="due_date"
                    label="Data de Vencimento"
                >
                    <DatePicker
                        size="large"
                        placeholder="Data de Vencimento"
                        format={"DD/MM/YYYY"}
                        style={{ width: "100%" }}
                        onChange={onChangeDueDate}
                    />
                </Form.Item>
            </Col>

            <Col
                lg={{ span: 6 }}
                md={{ span: 6 }}
                sm={{ span: 12 }}
                xs={{ span: 12 }}
            >
                <Form.Item label="Despesa Paga?" name="paid" valuePropName="checked">
                    <Switch />
                </Form.Item>
            </Col>

            {isPaid && (
                <>
                    <Col
                        lg={{ span: 6 }}
                        md={{ span: 6 }}
                        sm={{ span: 12 }}
                        xs={{ span: 12 }}
                    >
                        <Form.Item
                            name="payment_date"
                            label="Data de Pagamento"
                            rules={[{ required: true, message: "Campo obrigatório" }]}
                        >
                            <DatePicker
                                size="large"
                                placeholder="Data de Pagamento"
                                format={"DD/MM/YYYY"}
                                style={{ width: "100%" }}
                                onChange={onChangePaymentDate}
                            />
                        </Form.Item>
                    </Col>

                    <Col
                        lg={{ span: 6 }}
                        md={{ span: 6 }}
                        sm={{ span: 12 }}
                        xs={{ span: 12 }}
                    >
                        <Form.Item
                            label="Valor Pago"
                            name="amount_paid"
                            rules={[{ required: true, message: "Campo obrigatório" }]}
                        >
                            <InputNumber
                                min={0}
                                size="large"
                                placeholder="Valor Pago"
                                maxLength={6}
                                addonAfter={<Text style={{ color: "#8C8C8C" }}>R$</Text>}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </Col>
                </>
            )}

            <Divider />

            {/* <Col
                lg={{ span: 6 }}
                md={{ span: 6 }}
                sm={{ span: 12 }}
                xs={{ span: 12 }}
            >
                <Form.Item label="Fixa?" name="is_fixed" valuePropName="checked">
                    <Switch />
                </Form.Item>
            </Col> */}

            <Col
                lg={{ span: 6 }}
                md={{ span: 6 }}
                sm={{ span: 12 }}
                xs={{ span: 12 }}
            >
                <Form.Item label="Recorrente?" name="is_recurring" valuePropName="checked">
                    <Switch />
                </Form.Item>
            </Col>

            {isRecurring && (
                <>
                    <Col
                        lg={{ span: 6 }}
                        md={{ span: 6 }}
                        sm={{ span: 12 }}
                        xs={{ span: 12 }}
                    >
                        <Form.Item
                            label="Frequência"
                            name="frequency"
                            rules={[{ required: true, message: "Campo obrigatório" }]}
                        >
                            <Select placeholder="Frequência" size="large">
                                {frequencies.map((frequency: any) => (
                                    <Select.Option key={frequency.value} value={frequency.value}>
                                        {frequency.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col
                        lg={{ span: 6 }}
                        md={{ span: 6 }}
                        sm={{ span: 12 }}
                        xs={{ span: 12 }}
                    >
                        <Form.Item
                            label="Parcelas"
                            name="installments"
                        >
                            <InputNumber
                                min={1}
                                size="large"
                                placeholder="Parcelas"
                                maxLength={3}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </Col>
                </>
            )}

          </Row>
        </Form>
      </div>

      <Space>
        <Button
          type="primary"
          loading={createExpenseLoading || updateExpenseLoading}
          size="large"
          onClick={onSubmit}
        >
          Gravar
        </Button>

        <Button
          loading={createExpenseLoading || updateExpenseLoading}
          onClick={() => props.closeForm()}
          size="large"
        >
          Cancelar
        </Button>
      </Space>
    </>
  );
}
